import React, { useCallback, useEffect } from "react";
import Typography from "@carrier-io/air-react/Typography";
import Grid from "@carrier-io/air-react/Grid";
import { SetNewPasswordInput } from "../../validations/setNewPasswordValidation";
import Box from "@carrier-io/air-react/Box";
import { SubmitButton } from "../../components/submitButton/SubmitButton";
import { useNavigate, useSearchParams } from "react-router-dom";
import { RoutesPage } from "../../constants/routes";
import { Clients, useMutation } from "../../apollo";
import { EmailLookupResponse } from "../../generated/graphql";
import { EmailLookupMutation } from "../../graphql/mutations/emailLookup";
import { AuthMethodEnum } from "../../constants/authMethod";
import { useAlerts } from "../../contexts/alert/AlertsContext";
import { AlertTypes } from "../../contexts/alert/types";
import { Skeleton } from "@carrier-io/air-react";
import { DEFAULT_REGION } from "../../constants/regions";
import {
  ChangePasswordMutation,
  ChangePasswordMutationResponse,
  ChangePasswordMutationVariables,
} from "../../graphql/mutations/changePassword";
import { deleteLoginCookies } from "../../utils/cookie";
import { EmailChangeAccount } from "../../components/emailChangeAccount/emailChangeAccount";
import { SetNewPasswordForm } from "../../components/setNewPasswordForm/SetNewPasswordForm";

export const SetNewPassword = () => {
  const { pushAlert } = useAlerts();
  const [runEmailLookup, emailLookupResult] = useMutation<
    { emailLookup: EmailLookupResponse },
    {
      email: string;
      origin: string;
    }
  >(Clients.Authn, EmailLookupMutation);
  const [runChangePassword, changePasswordResult] = useMutation<
    ChangePasswordMutationResponse,
    ChangePasswordMutationVariables
  >(Clients.Authn, ChangePasswordMutation);
  const navigate = useNavigate();
  const [searchParam] = useSearchParams();
  const [password, email] = [
    searchParam.get("password"),
    searchParam.get("email"),
  ];
  const expireAt = Number(searchParam.get("expireAt"));
  const isLinkMalformed = !email || !password;
  const isLinkExpired = expireAt < Date.now();
  const isLinkFail = isLinkMalformed || isLinkExpired;

  useEffect(() => {
    if (isLinkFail) {
      const params = new URLSearchParams(searchParam);

      if (isLinkExpired) {
        params.set(
          "prevError",
          "This password set link has expired. Please request a new link.",
        );
      }
      if (isLinkMalformed) {
        params.set(
          "prevError",
          "Your set password link was malformed. Please, try again or request another invitation link.",
        );
      }

      navigate(
        {
          pathname: RoutesPage.Login,
          search: params.toString(),
        },
        { replace: true },
      );
    }
  }, [navigate, isLinkExpired, isLinkFail, isLinkMalformed, searchParam]);

  useEffect(() => {
    if (!email || !password) {
      navigate(RoutesPage.Login);
      return;
    }

    const verifyIsCredentialsAuthMethod = async () => {
      try {
        const { data } = await runEmailLookup({
          variables: { email, origin: window.location.origin },
        });
        const authMethod = data?.emailLookup.method;
        if (!authMethod || authMethod !== AuthMethodEnum.CREDENTIALS) {
          navigate(RoutesPage.Login);
        }
      } catch {
        pushAlert(AlertTypes.ERROR, "Cannot verify your auth method");
        navigate(RoutesPage.Login);
      }
    };

    verifyIsCredentialsAuthMethod();
  }, [password, email, navigate, runEmailLookup, pushAlert]);

  const handleSetNewPassword = async (data: SetNewPasswordInput) => {
    if (!emailLookupResult.data?.emailLookup.username) {
      pushAlert(AlertTypes.ERROR, "Failed to set new password");
      return;
    }

    try {
      await runChangePassword({
        variables: {
          username: emailLookupResult.data.emailLookup.username,
          newPassword: data.password,
          previousPassword: password as string,
          region: DEFAULT_REGION,
        },
      });

      deleteLoginCookies(window.location.hostname);
      pushAlert(AlertTypes.SUCCESS, "Password changed successfully");
      navigate(RoutesPage.Login);
    } catch (error: unknown) {
      if (error instanceof Error) {
        pushAlert(AlertTypes.ERROR, error.message);
        return;
      }
      pushAlert(AlertTypes.ERROR, "Failed to set new password");
    }
  };

  const isLoading = changePasswordResult.loading;

  const renderSubmitButton = useCallback(
    (isValid: boolean) => (
      <SubmitButton isLoading={isLoading} disabled={!isValid || isLoading}>
        Sign in
      </SubmitButton>
    ),
    [isLoading],
  );
  return (
    <>
      <Grid>
        <Typography variant="body2">Set your password</Typography>
      </Grid>
      {emailLookupResult.loading ? (
        <Skeleton
          data-testid="loading-skeleton"
          width={338}
          height={423}
          sx={{ transform: "scale(1)" }}
        />
      ) : (
        <Box maxWidth="338px" width="100%">
          <Box marginTop="20px" width="100%">
            <EmailChangeAccount email={email ?? ""} />
          </Box>
          <SetNewPasswordForm
            onSubmit={handleSetNewPassword}
            submitButton={renderSubmitButton}
          />
        </Box>
      )}
    </>
  );
};
